/**
 * Better Me cycle-tracking domain logic.
 *
 * Ships with a deterministic seed of realistic symptom logs (the past ~62 days)
 * so the app feels alive on first open. Predictions are derived from the logged
 * period history: average cycle length -> next period start -> flare-up window.
 */

export type FlowLevel = 'none' | 'light' | 'medium' | 'heavy';

export interface DayLog {
  /** ISO date, yyyy-mm-dd */
  date: string;
  /** Pain severity 0-10 */
  pain: number;
  flow: FlowLevel;
  /** Resting heart rate (bpm), simulated Health Connect / HealthKit sync */
  heartRate?: number;
  /** Heart-rate variability (ms) */
  hrv?: number;
  symptoms: string[];
  note?: string;
}

export const SYMPTOM_OPTIONS = [
  'Cramps',
  'Back pain',
  'Fatigue',
  'Headache',
  'Nausea',
  'Bloating',
  'Mood swings',
  'Brain fog',
] as const;

export const FLOW_LABELS: Record<FlowLevel, string> = {
  none: 'None',
  light: 'Light',
  medium: 'Medium',
  heavy: 'Heavy',
};

export const FLOW_WEIGHT: Record<FlowLevel, number> = {
  none: 0,
  light: 1,
  medium: 2,
  heavy: 3,
};

/* ------------------------------------------------------------------ */
/* Date helpers                                                        */
/* ------------------------------------------------------------------ */

export function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseISODate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

export function addDays(base: Date, days: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d;
}

/** Whole days from `a` to `b` (b - a), ignoring time-of-day. */
export function daysBetween(a: Date, b: Date): number {
  const ms =
    Date.UTC(b.getFullYear(), b.getMonth(), b.getDate()) -
    Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  return Math.round(ms / 86_400_000);
}

export function addDaysISO(iso: string, days: number): string {
  return toISODate(addDays(parseISODate(iso), days));
}

/** "Mon, Sep 22" */
export function formatShort(iso: string): string {
  return parseISODate(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** "Monday, September 22" */
export function formatLong(iso: string): string {
  return parseISODate(iso).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

/* ------------------------------------------------------------------ */
/* Labels                                                              */
/* ------------------------------------------------------------------ */

export function painLabel(pain: number): string {
  if (pain <= 0) return 'No pain';
  if (pain <= 3) return 'Mild';
  if (pain <= 6) return 'Moderate';
  return 'Severe';
}

/* ------------------------------------------------------------------ */
/* Seeded mock history                                                 */
/* ------------------------------------------------------------------ */

/** Deterministic pseudo-random in [0, 1) from a numeric seed. */
function noise(seed: number): number {
  const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

/** Period start offsets (relative to today) producing ~28-day cycles. */
const PERIOD_START_OFFSETS = [-81, -53, -25];

interface CycleProfile {
  pain: number;
  flow: FlowLevel;
  symptoms: string[];
}

/** Base symptom profile for a 0-based day within a 28-day cycle. */
function cycleProfile(cycleDay: number): CycleProfile {
  switch (cycleDay) {
    case 0:
      return { pain: 8, flow: 'heavy', symptoms: ['Cramps', 'Back pain', 'Fatigue', 'Nausea'] };
    case 1:
      return { pain: 7, flow: 'heavy', symptoms: ['Cramps', 'Back pain', 'Fatigue'] };
    case 2:
      return { pain: 6, flow: 'medium', symptoms: ['Cramps', 'Fatigue'] };
    case 3:
      return { pain: 5, flow: 'medium', symptoms: ['Cramps', 'Back pain'] };
    case 4:
      return { pain: 3, flow: 'light', symptoms: ['Fatigue'] };
    case 13:
      return { pain: 3, flow: 'none', symptoms: ['Cramps', 'Bloating'] };
    case 14:
      return { pain: 2, flow: 'none', symptoms: ['Bloating'] };
    case 21:
      return { pain: 3, flow: 'none', symptoms: ['Bloating', 'Mood swings'] };
    case 22:
      return { pain: 3, flow: 'none', symptoms: ['Mood swings', 'Brain fog'] };
    case 23:
      return { pain: 4, flow: 'none', symptoms: ['Cramps', 'Mood swings', 'Fatigue'] };
    case 24:
      return { pain: 4, flow: 'none', symptoms: ['Cramps', 'Back pain', 'Brain fog'] };
    case 25:
      return { pain: 5, flow: 'none', symptoms: ['Cramps', 'Back pain', 'Fatigue'] };
    case 26:
      return { pain: 6, flow: 'none', symptoms: ['Cramps', 'Back pain', 'Fatigue', 'Bloating'] };
    case 27:
      return { pain: 6, flow: 'none', symptoms: ['Cramps', 'Nausea', 'Fatigue', 'Bloating'] };
    default:
      if (cycleDay >= 5 && cycleDay <= 9) return { pain: 1, flow: 'none', symptoms: [] };
      if (cycleDay >= 15 && cycleDay <= 20) return { pain: 1, flow: 'none', symptoms: [] };
      return { pain: 2, flow: 'none', symptoms: [] };
  }
}

/**
 * Build ~62 days of realistic logs ending today. Deterministic for a given
 * date so the data doesn't jump around between reloads within a day.
 */
export function generateSeedLogs(today: Date = new Date()): DayLog[] {
  const logs: DayLog[] = [];

  for (let offset = -62; offset <= 0; offset++) {
    const date = addDays(today, offset);
    const epochDay = Math.floor(date.getTime() / 86_400_000);

    // Position within the current cycle.
    const start = [...PERIOD_START_OFFSETS].reverse().find((s) => s <= offset) ?? PERIOD_START_OFFSETS[0];
    const cycleDay = offset - start;

    const profile = cycleProfile(cycleDay);
    const jitter = Math.round(noise(epochDay) * 2 - 1); // -1..1
    const pain = Math.max(0, Math.min(10, profile.pain + (profile.pain > 0 ? jitter : 0)));

    // Skip some uneventful days so the history looks naturally logged.
    const quietDay = profile.pain <= 1 && profile.flow === 'none';
    if (quietDay && noise(epochDay + 999) < 0.35) continue;

    const heartRate = Math.round(61 + pain * 1.4 + noise(epochDay + 7) * 5);
    const hrv = Math.max(28, Math.round(60 - pain * 3.2 + noise(epochDay + 13) * 7));

    logs.push({
      date: toISODate(date),
      pain,
      flow: profile.flow,
      heartRate,
      hrv,
      symptoms: profile.symptoms,
    });
  }

  return logs;
}

/* ------------------------------------------------------------------ */
/* Prediction                                                          */
/* ------------------------------------------------------------------ */

export interface CyclePrediction {
  /** Average cycle length in days (from logged period starts). */
  cycleLength: number;
  /** All detected period-start dates (ISO). */
  periodStarts: string[];
  /** Most recent period start (ISO). */
  lastPeriodStart: string;
  /** Predicted next period start (ISO). */
  nextPeriodStart: string;
  /** Predicted high-pain days around the next period (ISO). */
  flareDates: string[];
  /** Days from today until the next predicted period (negative if started). */
  daysUntilPeriod: number;
  /** True when today falls inside the predicted flare window. */
  inFlareWindow: boolean;
}

/** Detect period starts: the first heavier-flow day of each flow run. */
export function detectPeriodStarts(logs: DayLog[]): string[] {
  const byDate = new Map(logs.map((l) => [l.date, l]));
  const sorted = [...logs].sort((a, b) => a.date.localeCompare(b.date));
  const starts: string[] = [];

  for (const log of sorted) {
    if (log.flow !== 'medium' && log.flow !== 'heavy') continue;
    const prev = byDate.get(addDaysISO(log.date, -1));
    const prevLight = !prev || prev.flow === 'none' || prev.flow === 'light';
    const lastStart = starts[starts.length - 1];
    const farEnough =
      !lastStart || daysBetween(parseISODate(lastStart), parseISODate(log.date)) >= 15;
    if (prevLight && farEnough) starts.push(log.date);
  }

  return starts;
}

/** Manual overrides the user can set in cycle settings. */
export interface CycleOverrides {
  /** ISO date the last period started (replaces auto-detection). */
  lastPeriodStart?: string;
  /** Cycle length in days (replaces the average computed from logs). */
  cycleLength?: number;
}

export function predictCycle(
  logs: DayLog[],
  today: Date = new Date(),
  overrides?: CycleOverrides,
): CyclePrediction | null {
  let periodStarts = detectPeriodStarts(logs);
  if (overrides?.lastPeriodStart) {
    periodStarts = [
      ...periodStarts.filter((d) => d < overrides.lastPeriodStart!),
      overrides.lastPeriodStart,
    ].sort();
  }
  if (periodStarts.length === 0) return null;

  const gaps: number[] = [];
  for (let i = 1; i < periodStarts.length; i++) {
    gaps.push(daysBetween(parseISODate(periodStarts[i - 1]), parseISODate(periodStarts[i])));
  }
  const detectedLength =
    gaps.length > 0 ? Math.round(gaps.reduce((a, b) => a + b, 0) / gaps.length) : 28;

  const overrideLength = overrides?.cycleLength;
  const cycleLength =
    overrideLength && overrideLength >= 15 && overrideLength <= 90
      ? Math.round(overrideLength)
      : detectedLength;

  const lastPeriodStart = periodStarts[periodStarts.length - 1];

  // Roll forward in whole cycles until the predicted window isn't fully past,
  // so the prediction always points at the next upcoming period.
  let nextPeriodStart = addDaysISO(lastPeriodStart, cycleLength);
  while (daysBetween(today, parseISODate(nextPeriodStart)) < -3) {
    nextPeriodStart = addDaysISO(nextPeriodStart, cycleLength);
  }

  // Flare-up window: the day before bleeding starts through day 3.
  const flareDates = [-1, 0, 1, 2, 3].map((d) => addDaysISO(nextPeriodStart, d));

  const todayISO = toISODate(today);
  const daysUntilPeriod = daysBetween(today, parseISODate(nextPeriodStart));

  return {
    cycleLength,
    periodStarts,
    lastPeriodStart,
    nextPeriodStart,
    flareDates,
    daysUntilPeriod,
    inFlareWindow: flareDates.includes(todayISO),
  };
}

/**
 * Predicted flare dates for future cycles, used by the calendar when
 * browsing ahead. Includes cycle offsets 0..3 (next 4 predicted periods).
 */
export function predictedFlareDates(prediction: CyclePrediction, cyclesAhead = 4): Set<string> {
  const dates = new Set<string>();
  for (let cycle = 0; cycle < cyclesAhead; cycle++) {
    const start = addDaysISO(prediction.nextPeriodStart, cycle * prediction.cycleLength);
    for (const d of [-1, 0, 1, 2, 3]) {
      dates.add(addDaysISO(start, d));
    }
  }
  return dates;
}

/**
 * Predicted period days (bleeding days only, no pain assumptions) for the
 * next few cycles. Used in gentle mode for people who don't get flare-ups.
 */
export function predictedPeriodDates(prediction: CyclePrediction, cyclesAhead = 4): Set<string> {
  const dates = new Set<string>();
  for (let cycle = 0; cycle < cyclesAhead; cycle++) {
    const start = addDaysISO(prediction.nextPeriodStart, cycle * prediction.cycleLength);
    for (const d of [0, 1, 2, 3, 4]) {
      dates.add(addDaysISO(start, d));
    }
  }
  return dates;
}

export interface CycleDayInfo {
  /** 1-based day within the current cycle. */
  day: number;
  phase: 'Menstrual' | 'Follicular' | 'Ovulation' | 'Luteal';
}

/** Where today falls within the current cycle, based on the last period start. */
export function cycleDayAndPhase(
  prediction: CyclePrediction,
  today: Date = new Date(),
): CycleDayInfo {
  let start = prediction.lastPeriodStart;
  // Roll forward past the current cycle boundary if needed.
  while (daysBetween(parseISODate(start), today) >= prediction.cycleLength) {
    start = addDaysISO(start, prediction.cycleLength);
  }
  const day = daysBetween(parseISODate(start), today) + 1;

  let phase: CycleDayInfo['phase'];
  if (day <= 5) phase = 'Menstrual';
  else if (day <= 12) phase = 'Follicular';
  else if (day <= 15) phase = 'Ovulation';
  else phase = 'Luteal';

  return { day, phase };
}
